import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  PackageCheck,
  Truck,
  Wallet,
  WashingMachine,
} from "lucide-react";
import { formatMinorUnits } from "@laundrypro/shared";
import {
  BrandLoader,
  Button,
  Card,
  CardHeader,
  EmptyState,
  PageHeader,
  Skeleton,
  SkeletonCard,
  StatTile,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from "../components/ui";
import { BrandLogo } from "../components/BrandLogo";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { ApiError, apiFetch } from "../lib/api";

interface DashboardSummary {
  domainReady: boolean;
  orders: {
    receivedToday: number;
    processing: number;
    readyForPickup: number;
    deliveredToday: number;
  };
  money: { revenueTodayMinor: number; outstandingMinor: number; currency: string };
  recentOrders: readonly {
    id: string;
    number: string;
    customerName: string;
    status: string;
    totalMinor: number;
    createdAt: string;
  }[];
  setup: {
    activeUsers: number;
    roles: number;
    vatConfigured: boolean;
    businessIdentityComplete: boolean;
  };
}

/**
 * The operational dashboard.
 *
 * Deliberately not an analytics screen. There are no charts, no trend arrows and
 * no percentage deltas — a laundry counter needs to know what is in the shop
 * right now, not how this week compares to last. Every figure here is something
 * a person acts on within the hour.
 */
export function DashboardPage() {
  const { user } = useAuth();
  const { language, branding } = useBranding();
  const ar = language === "ar";

  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSummary(await apiFetch<DashboardSummary>("/dashboard/summary"));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : ar ? "تعذّر الاتصال." : "Connection failed.");
    } finally {
      setLoading(false);
    }
  }, [ar]);

  useEffect(() => {
    void load();
  }, [load]);

  const currency = summary?.money.currency ?? "SAR";
  const currencyLabel = ar ? "ر.س" : currency;

  if (loading && !summary) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-7 w-40" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Skeleton className="h-44 w-full rounded-2xl lg:col-span-2" />
          <SkeletonCard />
        </div>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <Card>
        <EmptyState
          icon={<AlertTriangle className="size-6" />}
          title={ar ? "تعذّر تحميل لوحة التحكم" : "Couldn't load the dashboard"}
          description={error ?? (ar ? "حاول تحديث الصفحة." : "Try refreshing the page.")}
          action={
            <Button size="sm" onClick={load}>
              {ar ? "إعادة المحاولة" : "Retry"}
            </Button>
          }
        />
      </Card>
    );
  }

  const setupItems = [
    {
      done: summary.setup.businessIdentityComplete,
      labelAr: "بيانات المنشأة",
      labelEn: "Business identity",
      hintAr: "الاسم والرقم الضريبي والعنوان",
      hintEn: "Name, VAT number and address",
    },
    {
      done: summary.setup.vatConfigured,
      labelAr: "إعدادات الضريبة",
      labelEn: "Tax settings",
      hintAr: "تفعيل ضريبة القيمة المضافة",
      hintEn: "VAT enabled and rate set",
    },
    {
      done: summary.setup.activeUsers > 1,
      labelAr: "فريق العمل",
      labelEn: "Your team",
      hintAr: `${summary.setup.activeUsers} حساب نشط`,
      hintEn: `${summary.setup.activeUsers} active account(s)`,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={ar ? branding.appNameAr : branding.appNameEn}
        title={ar ? "لوحة التحكم" : "Dashboard"}
        subtitle={
          ar ? `أهلاً، ${user?.fullName ?? ""}` : `Welcome back, ${user?.fullName ?? ""}`
        }
        leading={<BrandLogo size={40} className="hidden max-h-10 sm:block" />}
      />

      {/*
        Until the laundry domain exists there is nothing operational to report.
        Saying so replaces the KPI blocks entirely — showing four zeroed tiles
        would look like a working shop with no customers, which is a different
        and much worse message.
      */}
      {!summary.domainReady ? (
        <>
          <Card padding="none">
            <EmptyState
              watermark
              icon={<WashingMachine className="size-6" />}
              title={ar ? "لم تبدأ العمليات بعد" : "Operations haven't started yet"}
              description={
                ar
                  ? "بمجرد استقبال أول طلب ستظهر هنا حالة التشغيل والإيرادات والمبالغ المستحقة."
                  : "Once you take in your first order, live counts, revenue and outstanding balances appear here."
              }
            />
          </Card>

          {/* Real, actionable Phase 1 state — not filler. Each row is something
              the owner can go and finish right now. */}
          <Card>
            <CardHeader
              title={ar ? "إعداد المنشأة" : "Set up your business"}
              description={
                ar
                  ? "أكمل هذه الخطوات قبل استقبال أول طلب."
                  : "Finish these before taking your first order."
              }
            />
            <ul className="space-y-1.5">
              {setupItems.map((item) => (
                <li
                  key={item.labelEn}
                  className="flex items-center justify-between gap-3 border-b border-line-hairline py-2.5 last:border-0"
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    {item.done ? (
                      <CheckCircle2 className="size-4 shrink-0 text-success" aria-hidden="true" />
                    ) : (
                      <span
                        className="size-4 shrink-0 rounded-full border border-line-strong"
                        aria-hidden="true"
                      />
                    )}
                    <div className="min-w-0">
                      <div className="truncate text-sm text-ink-800">
                        {ar ? item.labelAr : item.labelEn}
                      </div>
                      <div className="truncate text-xs text-muted-subtle">
                        {ar ? item.hintAr : item.hintEn}
                      </div>
                    </div>
                  </div>
                  <span className="shrink-0 text-xs font-medium text-muted-foreground">
                    {item.done ? (ar ? "مكتمل" : "Done") : ar ? "غير مكتمل" : "Pending"}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </>
      ) : (
        <>
          {/*
            The hero. Today's revenue is the single number most looked at, so it
            gets a dark panel spanning two of three columns while everything else
            is a tile — hierarchy by area and contrast rather than by font size
            alone.
          */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="relative overflow-hidden rounded-2xl border border-graphite-700 bg-sidebar p-6 text-white shadow-md lg:col-span-2">
              <div
                className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-l from-transparent via-white/10 to-transparent"
                aria-hidden="true"
              />
              <div
                className="pointer-events-none absolute -top-16 end-[-4rem] size-40 rounded-full bg-accent-500/[0.06] blur-2xl"
                aria-hidden="true"
              />
              <div className="relative flex items-center justify-between">
                <span className="text-xs font-medium tracking-wide text-ink-300">
                  {ar ? "إيرادات اليوم" : "Today's revenue"}
                </span>
                <span className="flex size-9 items-center justify-center rounded-xl bg-accent-500/15 text-accent-300 ring-1 ring-inset ring-accent-400/20">
                  <Wallet className="size-[18px]" aria-hidden="true" />
                </span>
              </div>
              <div className="tabular-nums relative mt-4 flex items-baseline gap-1.5">
                <span className="text-[2.75rem] font-bold leading-none tracking-tightest">
                  {formatMinorUnits(summary.money.revenueTodayMinor)}
                </span>
                {/* A separate, smaller span — never concatenated into the number,
                    so the digits stay one unbroken run in RTL. */}
                <span className="text-lg font-normal text-muted-subtle">{currencyLabel}</span>
              </div>
              <div className="relative mt-3 flex items-center gap-3 text-xs text-muted-subtle">
                <span className="tabular-nums">
                  {ar
                    ? `${summary.orders.receivedToday} طلب اليوم`
                    : `${summary.orders.receivedToday} orders today`}
                </span>
                <span className="text-ink-600" aria-hidden="true">
                  ·
                </span>
                <span className="tabular-nums">
                  {ar ? "مستحق: " : "Outstanding: "}
                  {formatMinorUnits(summary.money.outstandingMinor)} {currencyLabel}
                </span>
              </div>
            </div>

            <StatTile
              label={ar ? "جاهز للاستلام" : "Ready for pickup"}
              value={String(summary.orders.readyForPickup)}
              hint={ar ? "بانتظار العميل" : "Waiting on the customer"}
              icon={<PackageCheck className="size-4" />}
              tone="accent"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatTile
              label={ar ? "استُلمت اليوم" : "Received today"}
              value={String(summary.orders.receivedToday)}
              icon={<ClipboardList className="size-4" />}
            />
            <StatTile
              label={ar ? "قيد التشغيل" : "Processing"}
              value={String(summary.orders.processing)}
              icon={<WashingMachine className="size-4" />}
            />
            <StatTile
              label={ar ? "سُلّمت اليوم" : "Delivered today"}
              value={String(summary.orders.deliveredToday)}
              icon={<Truck className="size-4" />}
            />
            <StatTile
              label={ar ? "المبالغ المستحقة" : "Outstanding"}
              value={`${formatMinorUnits(summary.money.outstandingMinor)} ${currencyLabel}`}
              icon={<Wallet className="size-4" />}
            />
          </div>

          <Card padding="none">
            <div className="p-5 pb-0 sm:p-6 sm:pb-0">
              <CardHeader
                title={ar ? "أحدث الطلبات" : "Recent orders"}
                description={ar ? "آخر ١٠ طلبات" : "The last 10 orders taken in"}
              />
            </div>
            {summary.recentOrders.length === 0 ? (
              <EmptyState
                icon={<ClipboardList className="size-6" />}
                title={ar ? "لا توجد طلبات بعد" : "No orders yet"}
                description={
                  ar ? "ستظهر الطلبات هنا فور استلامها." : "Orders appear here as soon as they are taken in."
                }
              />
            ) : (
              <div className="px-5 pb-5 sm:px-6 sm:pb-6">
                <Table>
                  <THead>
                    <tr>
                      <Th>{ar ? "رقم الطلب" : "Order"}</Th>
                      <Th>{ar ? "العميل" : "Customer"}</Th>
                      <Th>{ar ? "الحالة" : "Status"}</Th>
                      <Th>{ar ? "الإجمالي" : "Total"}</Th>
                      <Th>{ar ? "التاريخ" : "Date"}</Th>
                    </tr>
                  </THead>
                  <tbody>
                    {summary.recentOrders.map((order) => (
                      <Tr key={order.id}>
                        <Td className="tabular-nums font-medium text-ink-900">#{order.number}</Td>
                        <Td className="font-medium text-ink-900">{order.customerName}</Td>
                        <Td>{order.status}</Td>
                        <Td className="tabular-nums font-medium">
                          {formatMinorUnits(order.totalMinor)} {currencyLabel}
                        </Td>
                        <Td className="tabular-nums text-muted-foreground">{order.createdAt}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

export { BrandLoader };
